<template>
  <PageLayout>
    <template v-slot:header>
      <div class="ui padded grid">
        <div class="row">
          <div class="eight wide column">
            <Breadcrumb>
              <a v-go-back="'contracts'">Contratos</a>
              <i class="right chevron icon divider"></i>
              <div class="active section">{{ sectionName }}</div>
            </Breadcrumb>
          </div>
          <div v-show="persisted" class="eight wide right aligned column">
            <div class="actions">
              <a
                class="section"
                :href="$router.resolve({ name: 'newContract' }).href"
              >
                <i class="plus icon"></i>Novo Contrato
              </a>
              <a @click.prevent="openHistory">
                <i class="history icon"></i>Histórico
              </a>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:main>
      <HistoryModal ref="history" resource="Contract" :item_id="form.id" />
      <TransactionModal ref="transactions" />
      <UserSearchModal
        ref="client_search_modal"
        title="Selecionar um titular"
        @selected="item => setOwner(item)"
      />
      <UserSearchModal
        ref="seller_search_modal"
        :rule="['collaborator', 'seller']"
        title="Selecionar um vendedor"
        @selected="item => setSeller(item)"
      />
      <UserSearchModal
        ref="member_search_modal"
        title="Selecionar um beneficiário"
        @selected="item => setMember(item)"
      />
      <div v-show="form.state == 'canceled'" class="ui padded grid">
        <div class="ten wide column">
          <div class="ui warning message">
            <div class="header">Contrato cancelado</div>
            {{ form.cancellation_reason }}
            <p v-show="settings.contract_allows_enable_canceled_contract">
              <a @click.prevent="enableContract"><b>Ativar Contrato</b></a>
            </p>
          </div>
        </div>
      </div>

      <form
        :class="['ui small', { error: anyError }, 'form']"
        v-autocomplete-off
      >
        <div class="ui padded grid">
          <div v-show="loading" class="ui active inverted dimmer">
            <div class="ui text loader">Carregando...</div>
          </div>
          <div class="row">
            <div class="ten wide column">
              <div class="ui fields">
                <div
                  v-show="settings.contract_enable_method == 'manual'"
                  :class="[
                    'four wide required',
                    { error: errors.state },
                    'field'
                  ]"
                  :data-content="errorsTo('state')"
                >
                  <label for="contrato-form-state"> Situação </label>
                  <select
                    id="contrato-form-state"
                    name="state"
                    v-model="form.state"
                  >
                    <option
                      v-for="(state, index) in states"
                      :key="index"
                      :value="state.name"
                    >
                      {{ state.description }}
                    </option>
                  </select>
                </div>
                <div
                  :class="[
                    'four wide required',
                    { error: errors.date },
                    'field'
                  ]"
                  :data-content="errorsTo('date')"
                >
                  <label for="contract-form-date">Emissão</label>
                  <input
                    type="text"
                    v-mask="'##/##/####'"
                    id="contract-form-date"
                    v-model="form.date"
                  />
                </div>
                <div
                  v-if="enableCardNumber"
                  :class="['four wide', { error: errors.card_number }, 'field']"
                  :data-content="errorsTo('card_number')"
                >
                  <label for="contract-form-date">Número do cartão</label>
                  <input
                    v-focus
                    type="text"
                    id="contract-form-card-number"
                    v-model="form.card_number"
                  />
                </div>
              </div>
              <div :class="[{ error: errors.owner }, 'fields']">
                <div
                  :class="[
                    'four wide required',
                    { error: errors.owner },
                    'field'
                  ]"
                  :data-content="errorsTo('owner')"
                >
                  <label for="contract-form-owner-id">Titular</label>
                  <div class="ui icon input">
                    <input
                      name="contract-form-owner-id"
                      v-focus
                      type="text"
                      id="contract-form-owner-id"
                      v-model="form.owner_id"
                      @blur="setUser($event.target.value, 'owner_name')"
                    />
                    <i
                      class="circular search link icon"
                      @click.prevent="$refs.client_search_modal.open"
                    ></i>
                  </div>
                </div>
                <div class="twelve wide end field">
                  <input
                    disabled
                    type="text"
                    id="contract-form-owner-name"
                    v-model="form.owner_name"
                  />
                </div>
              </div>
              <div :class="[{ error: errors.seller }, 'fields']">
                <div
                  :class="[
                    'four wide required',
                    { error: errors.seller },
                    'field'
                  ]"
                  :data-content="errorsTo('seller')"
                >
                  <label for="contract-form-owner-id">Vendedor</label>
                  <div class="ui icon input">
                    <input
                      name="contract-form-seller-id"
                      type="text"
                      id="contract-form-seller-id"
                      v-model="form.seller_id"
                      @blur="setUser($event.target.value, 'seller_name')"
                    />
                    <i
                      class="circular search link icon"
                      @click.prevent="$refs.seller_search_modal.open"
                    ></i>
                  </div>
                </div>
                <div class="twelve wide end field">
                  <input
                    disabled
                    type="text"
                    id="contract-form-seller-name"
                    v-model="form.seller_name"
                  />
                </div>
              </div>
              <div
                :class="[
                  'sixteen wide required',
                  { error: errors.plan },
                  'field'
                ]"
                :data-content="errorsTo('plan')"
              >
                <label for="contrato-form-plan">Plano</label>
                <select
                  id="contrato-form-plan"
                  name="plan"
                  v-model="form.plan_id"
                >
                  <option :value="null"></option>
                  <option
                    v-for="(plan, index) in enablePlans"
                    :key="index"
                    :value="plan.id"
                  >
                    {{ plan.id }} - {{ plan.description }}
                  </option>
                </select>
              </div>
              <div
                :class="[{ error: errors.about }, 'field']"
                :data-content="errorsTo('about')"
              >
                <label for="contrato-form-obs">Observações:</label>
                <textarea
                  id="contrato-form-obs"
                  name="obs"
                  v-model="form.obs"
                ></textarea>
              </div>
              <h4 class="ui dividing header">Faturamento</h4>
              <div class="fields">
                <div
                  :class="[
                    'six wide required',
                    { error: errors.payment_method },
                    'field'
                  ]"
                  :data-content="errorsTo('payment_method')"
                >
                  <label for="contrato-form-payment-method">
                    Forma de Pagamento
                  </label>
                  <select
                    id="contrato-form-payment-method"
                    name="payment_method"
                    v-model="form.payment_method.covenant"
                  >
                    <option :value="null"></option>
                    <option
                      v-for="(payment_method, index) in enablePaymentMethods"
                      :key="index"
                      :value="payment_method.name"
                    >
                      {{ payment_method.description }}
                    </option>
                  </select>
                </div>
                <div
                  :class="['two wide', { error: errors.due_day }, 'field']"
                  :data-content="errorsTo('due_day')"
                >
                  <label for="contrato-form-due-day" title="Dia de Vencimento">
                    Dia de Vencimento
                  </label>
                  <input
                    type="text"
                    id="contract-form-due-day"
                    name="due_day"
                    v-model="form.due_day"
                  />
                </div>
              </div>
              <div
                class="ui info message"
                v-show="
                  settings.contract_show_checkout_link && form.checkout_link
                "
              >
                <div class="header">Link para checkout</div>
                <div class="content">
                  {{ form.checkout_link }}
                </div>
              </div>
              <div v-show="showCreditCardInfo" class="ui info message">
                <div class="header">
                  {{ form.payment_method.card_holder_name }}
                </div>
                <div class="content">
                  <div>
                    Número:
                    {{ form.payment_method.card_number | shorCreditCard }}
                  </div>
                  <br />
                  <a
                    @click.prevent="changeCard"
                    v-show="settings.contract_allows_manual_credit_card == 't'"
                    ><b>Trocar Cartão de Crédito</b></a
                  >
                </div>
              </div>
              <div v-show="showCreditCardForm" class="ui fields">
                <div
                  :class="[
                    'six wide',
                    { error: errorsTo('payment_method.card_hash') },
                    'field'
                  ]"
                  :data-content="errorsTo('payment_method.card_hash')"
                >
                  <label for="contract-form-payment-method-card-holder-name"
                    >Título do Cartão</label
                  >
                  <input
                    type="text"
                    id="contract-form-payment-method-card-holder-name"
                    name="card_holder-name"
                    v-model="form.payment_method.card_holder_name"
                  />
                </div>
                <div class="five wide field">
                  <label for="contract-form-payment-method-card-number">
                    Número
                  </label>
                  <input
                    type="text"
                    id="contract-form-payment-method-card-number"
                    name="card_number"
                    v-model="form.payment_method.card_number"
                  />
                </div>
                <div class="two wide field">
                  <label for="contract-form-payment-method-card-cvv">CVV</label>
                  <input
                    type="text"
                    id="contract-form-payment-method-card-cvv"
                    name="card_cvv"
                    v-model="form.payment_method.card_cvv"
                  />
                </div>
                <div class="three wide field">
                  <label for="contract-form-payment-method-card-expiration"
                    >Validade</label
                  >
                  <input
                    type="text"
                    placeholder="MM/YY"
                    id="contract-form-payment-method-card-expiration"
                    name="card_expiration"
                    v-mask="'##/##'"
                    v-model="form.payment_method.card_expiration"
                  />
                </div>
              </div>
              <p v-show="persisted">
                <a @click.prevent="openTransactions">
                  <i class="eye icon"></i>Visualizar transações
                </a>
              </p>
              <div v-show="form.owner_person === 'individual'">
                <h4 class="ui dividing header">Beneficiários</h4>
                <div v-if="anyMembersError" class="ui error message">
                  <ul class="list">
                    <li
                      v-for="(msg, erroMemberIndex) in errors.members"
                      :key="erroMemberIndex"
                    >
                      {{ msg }}
                    </li>
                  </ul>
                </div>
                <div
                  v-for="(member, index) in members"
                  :key="index"
                  class="ui fields"
                >
                  <div class="four wide required field">
                    <label for="contract-form-owner-id">Beneficiário</label>
                    <div class="ui icon input">
                      <input
                        type="text"
                        class="member-user-id-input"
                        v-model="member.user_id"
                        @blur="
                          setMemberName($event.target.value, 'user_name', member)
                        "
                      />
                      <i
                        class="circular search link icon"
                        @click.prevent="openMemberSearchModel(member)"
                      ></i>
                    </div>
                  </div>
                  <div class="twelve wide end field">
                    <input disabled type="text" v-model="member.user_name" />
                  </div>
                  <div class="remove field">
                    <a
                      class="delete"
                      title="Remover"
                      @click.prevent="removeMember(member)"
                      ><i class="trash icon"></i
                    ></a>
                  </div>
                </div>
                <a @click.prevent="addMember">
                  <i class="plus icon"></i> Adicionar Beneficiário
                </a>
              </div>
            </div>
          </div>
          <!-- row -->
          <div class="row">
            <div class="sixteen wide column">
              <div class="ui divider"></div>
              <div class="field">
                <button
                  @click="save"
                  type="button"
                  name="save"
                  class="ui submit blue button"
                >
                  <i class="save icon"></i>Salvar
                </button>
                <a v-show="persisted" class="delete" @click.prevent="remove">
                  <i class="trash icon"></i>Remover Contrato
                </a>
                <a
                  v-show="showCancelOption"
                  class="warn"
                  @click.prevent="cancel"
                >
                  <i class="ban icon"></i>Cancelar Contrato
                </a>
              </div>
            </div>
          </div>
        </div>
        <!-- grid -->
      </form>
    </template>
  </PageLayout>
</template>

<style lang="css" scoped>
.warn {
  margin-right: 10px;
}
</style>

<script>
import Api from '@/resources/Api';
import Breadcrumb from '@/components/breadcrumb';
import UserSearchModal from '@/components/user_search_modal';
import PageLayout from '@/layouts/page_layout';
import HistoryModal from '@/components/history_modal';
import TransactionModal from './transaction_modal';
import Feature from '@/utils/features';
import { mapState, mapGetters } from 'vuex';
const sleep = ms => new Promise(res => setTimeout(res, ms));

export default {
  name: 'ContractForm',
  components: {
    Breadcrumb,
    PageLayout,
    UserSearchModal,
    HistoryModal,
    TransactionModal
  },

  filters: {
    shorCreditCard: function (value) {
      if (!value) return '';
      return `${value.substr(0, 4)}........${value.substr(12)}`;
    }
  },

  data() {
    return {
      enableCardNumber: Feature.allows('card_number'),
      loading: false,
      loadingCep: false,
      errors: {},
      plans: [],
      currentMemberIndex: null,
      newCreditCard: false,
      payment_methods: [],
      states: [
        { name: 'enable', description: 'Ativo' },
        { name: 'canceled', description: 'Cancelado' },
        { name: 'pending', description: 'Pendente' }
      ],
      form: {
        date: this.$moment().format('DD/MM/YYYY'),
        id: null,
        enable: false,
        owner_id: null,
        owner_name: null,
        owner_person: null,
        seller_id: null,
        seller_name: null,
        // seller_name: 't',
        obs: null,
        state: null,
        plan_id: null,
        cancellation_reason: null,
        card_number: null,
        checkout_link: null,
        payment_method: {
          id: null,
          new_card: false,
          covenant: null,
          card_hash: null,
          card_number: null,
          card_cvv: null,
          card_holder_name: null,
          card_expiration: ''
        },
        members: []
      }
    };
  },

  computed: {
    ...mapState({
      settings: state => state.settings.settings
    }),

    ...mapGetters({
      paymentMethods: 'settings/paymentMethods'
    }),

    enablePaymentMethods() {
      const exclusivePaymentMethodsValues = this.enablePlans
        .filter(x => x.payment_methods.length > 0)
        .map(x => x.payment_methods)
        .flat(1)
        .filter((v, i, a) => a.indexOf(v) === i);

      const geralPaymentMethods = this.paymentMethods.filter(
        x => !exclusivePaymentMethodsValues.includes(x.name)
      );

      // when selected plan has a exclusive payment_methods
      if (this.form.plan_id != null) {
        const plan = this.plans.find(x => x.id == this.form.plan_id);
        if (plan) {
          if (plan.payment_methods.length > 0) {
            return this.paymentMethods.filter(x =>
              plan.payment_methods.includes(x.name)
            );
          }
        }
      }

      return geralPaymentMethods;
    },

    currentPaymentMethod() {
      const covenant = this.form.payment_method.covenant;
      if (covenant == null) {
        return null;
      } else {
        return this.enablePaymentMethods.find(x => x.name == covenant);
      }
    },

    showCreditCardInfo() {
      if (this.currentPaymentMethod == null) {
        return false;
      } else {
        return (
          this.currentPaymentMethod.is_card &&
          this.form.payment_method.id != null &&
          !this.newCreditCard
        );
      }
      // (form.payment_method.covenant == 'card_pagar_me' ||
      //   form.payment_method.is_card) &&
      // form.payment_method.id != null &&
      // newCreditCard == false
    },

    showCreditCardForm() {
      if (this.currentPaymentMethod == null) {
        return false;
      } else {
        return (
          this.currentPaymentMethod.is_card &&
          (this.form.payment_method.id == null || this.newCreditCard) &&
          this.settings.contract_allows_manual_credit_card == 't'
        );
      }
    },

    persisted() {
      return this.form.id != null;
    },

    showCancelOption() {
      return (
        this.form.id != null &&
        this.form.state != null &&
        this.form.state != 'canceled'
      );
    },

    cardError() {
      return this.errors?.['payment_method.card_hash']?.[0];
    },

    sectionName() {
      return this.persisted
        ? `Editar Contrato - ${this.form.id}`
        : 'Novo Contrato';
    },

    anyError() {
      return !_.isEmpty(this.errors);
    },

    anyMembersError() {
      return (this.errors.members || []).length;
    },

    currentMember() {
      return this.currentMemberIndex != null
        ? this.form.members[this.currentMemberIndex]
        : null;
    },

    members() {
      return this.form.members.filter(b => !b._destroy);
    },

    enablePlans() {
      return this.plans.filter(p => p.enable || p.id == this.form.plan_id);
    }
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      this.getPlans();
      if (this.$route.params.id) {
        await this.fetch(this.$route.params.id);
      }
    },

    enableContract() {
      if (this.loading) {
        return;
      }

      Api.contracts
        .enable(this.form.id)
        .then(response => {
          this.$toast.show('Contrato ativo', 'success');
          this.form = this._parseContract(response.data.contract);
        })
        .catch(error => {
          if (error.response) {
            const errors = error.response.data.errors;
            const baseErrors = errors && errors.base;
            if (baseErrors && baseErrors.length) {
              this.$toast.show(baseErrors.join(', '), 'error');
            } else {
              this.$toast.show('Erro ao ativar contrato', 'error');
            }
            this._setErrors(errors);
          } else {
            throw error;
          }
        })
        .finally(() => {
          this.$scrollTop();
          this.loading = false;
        });
    },

    changeCard() {
      this.newCreditCard = true;
      this.form.payment_method.card_hash = null;
      this.form.payment_method.card_number = null;
      this.form.payment_method.card_cvv = null;
      this.form.payment_method.card_holder_name = null;
      this.form.payment_method.card_expiration = null;
      this.form.payment_method.new_card = true;
      setTimeout(() => {
        $('#contract-form-payment-method-card-holder-name').focus();
      }, 100);
    },

    openHistory() {
      this.$refs.history.open();
    },

    openTransactions() {
      this.$refs.transactions.open(this.form.id);
    },

    openMemberSearchModel(member) {
      this.currentMemberIndex = this.form.members.indexOf(member);
      this.$refs.member_search_modal.open();
    },

    removeMember(member) {
      if (member.id != null) {
        this.$set(member, '_destroy', true);
      } else {
        const realIndex = this.form.members.indexOf(member);
        this.$delete(this.form.members, realIndex);
      }
    },

    async addMember() {
      this.form.members.push({
        id: null,
        user_id: null,
        user_name: null,
        _destroy: false
      });
      await sleep(10);
      $(this.$el).find('.member-user-id-input:last').focus();
    },

    setOwner(item) {
      this.form.owner_id = item.id;
      this.form.owner_name = item.name;
      this.form.owner_person = item.person;
    },

    setSeller(item) {
      this.form.seller_id = item.id;
      this.form.seller_name = item.name;
    },

    setMember(item) {
      let member = this.currentMember;
      if (member) {
        member.user_id = item.id;
        member.user_name = item.name;
      }
    },

    errorsTo(key) {
      const errors = _.isEmpty(this.errors[key]) ? [] : this.errors[key];
      return errors.join(', ');
    },

    _setErrors(errors) {
      this.errors = _.clone(errors || {});
      setTimeout(() => {
        $('.error.field').popup();
      }, 100);
    },

    getPlans() {
      this.loading = true;
      const query = {
        q: {
          s: ['id asc']
        }
      };

      Api.plans
        .search(query)
        .then(async response => {
          this.plans = _.clone(response.data.plans);
        })
        .catch(error => {
          this.$toast.show('Erro ao buscar dados de serviços', 'error');
          throw error;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    save() {
      this.loading = true;
      this._setErrors({});

      let data = _.clone(this.form);

      // remove state to prevent concurrence bug
      delete data.state;

      data.members_attributes = data.members;

      data.payment_method_attributes = data.payment_method;

      const cardExpiration = data.payment_method.card_expiration;
      if (!_.isEmpty(cardExpiration)) {
        [
          data.payment_method_attributes.card_expiration_month,
          data.payment_method_attributes.card_expiration_year
        ] = cardExpiration.split('/');
      }

      // if (data.payment_method_attributes?.covenant == 'card_pagar_me') {
      //   const cardExpiration = data.payment_method.card_expiration;
      //   if (!_.isEmpty(cardExpiration)) {
      //     [
      //       data.payment_method_attributes.card_expiration_month,
      //       data.payment_method_attributes.card_expiration_year
      //     ] = cardExpiration.split('/');
      //   }
      // }

      Api.contracts
        .save(data)
        .then(async response => {
          this.$toast.show('Contrato salvo com sucesso', 'success');
          response.data.contract.payment_method =
            response.data.contract.payment_method || {};
          this.form = this._parseContract(response.data.contract);
        })
        .catch(error => {
          if (error.response) {
            const errors = error.response.data.errors;
            const baseErrors = errors && errors.base;
            if (baseErrors && baseErrors.length) {
              this.$toast.show(baseErrors.join(', '), 'error');
            } else {
              this.$toast.show('Erro ao salvar contrato', 'error');
            }
            this._setErrors(errors);
          } else {
            throw error;
          }
        })
        .finally(() => {
          this.$scrollTop();
          this.loading = false;
        });
    },

    cancel() {
      const confirmIt = confirm('Cancelar Contrato?');
      if (confirmIt) {
        this.loading = true;
        Api.contracts
          .cancel(this.form.id)
          .then(async () => {
            this.$toast.show('Contrato cancelado com sucesso', 'success');
            this.fetch(this.form.id);
            // this.$router.push({ name: 'contracts' });
          })
          .catch(error => {
            if (error.response) {
              const errors = error.response.data.errors;
              const baseErrors = errors && errors.base;
              if (baseErrors && baseErrors.length) {
                this.$toast.show(baseErrors.join(', '), 'error');
              } else {
                this.$toast.show('Erro ao cancelar contrato', 'error');
              }
              this._setErrors(errors);
            } else {
              throw error;
            }
          })
          .finally(() => {
            this.$scrollTop();
            this.loading = false;
          });
      }
    },

    remove() {
      const removeIt = confirm('Remover Contrato?');
      if (removeIt) {
        this.loading = true;
        Api.contracts
          .destroy(this.form.id)
          .then(async () => {
            this.$toast.show('Contrato removido com sucesso', 'success');
            this.$router.push({ name: 'contracts' });
          })
          .catch(error => {
            if (error.response) {
              const errors = error.response.data.errors;
              const baseErrors = errors && errors.base;
              if (baseErrors && baseErrors.length) {
                this.$toast.show(baseErrors.join(', '), 'error');
              } else {
                this.$toast.show('Erro ao remover contrato', 'error');
              }
              this._setErrors(errors);
            } else {
              throw error;
            }
          })
          .finally(() => {
            this.$scrollTop();
            this.loading = false;
          });
      }
    },

    _parseContract(contract) {
      contract.date = contract.date
        ? this.$moment(contract.date, 'YYYY-MM-DD').format('DD/MM/YYYY')
        : null;
      return contract;
    },

    fetch(id) {
      this.loading = true;
      Api.contracts
        .fetch(id)
        .then(async response => {
          response.data.contract.payment_method =
            response.data.contract.payment_method || {};
          this.form = this._parseContract(response.data.contract);
        })
        .catch(error => {
          if (error.response) {
            this.$toast.show('Erro ao buscar contrato', 'error');
            this.$router.push({ name: 'contracts' });
          } else {
            throw error;
          }
        })
        .finally(() => {
          this.$scrollTop();
          this.loading = false;
        });
    },

    async setUser(id, field) {
      try {
        await sleep(10);
        if (_.isEmpty(this.form[field]) && !_.isEmpty(id)) {
          const response = await Api.users.fetch(id);
          const user = response.data.user;
          this.form[field] = user.name;
          this.form.person = user.person;
        }
      } catch (err) {
        if (err.response) {
          this.$toast.show('Erro ao buscar usuário', 'error');
        } else {
          throw err;
        }
      }
    },

    async setMemberName(id, field, member = null) {
      try {
        await sleep(10);

        if (member && _.isEmpty(member[field]) && !_.isEmpty(id)) {
          const response = await Api.users.fetch(id);
          const user = response.data.user;
          member[field] = user.name;
        }
      } catch (err) {
        if (err.response) {
          this.$toast.show('Erro ao buscar usuário', 'error');
        } else {
          throw err;
        }
      }
    }
  }
};
</script>
